import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule } from '@angular/forms';
import { SubmitConfirmDialogComponent } from '../submit-confirm-dialog/submit-confirm-dialog.component';
import { QuizResultDialogComponent } from '../quiz-result-dialog/quiz-result-dialog.component';
import { TimeUpDialogComponent } from '../time-up-dialog/time-up-dialog.component';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { GetQuizByIdResponse } from '@edtech-ui/core/models/interfaces/attempt-Quiz/quiz-Question';
import { interval, of, Subscription, timer } from 'rxjs';
import { timeout, retry, catchError, tap, finalize, delay } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { SaveQuizProgressPayload } from '@edtech-ui/core/models/quiz-attempt/save-quiz-progress';
import { QuizQuestionWithAnswer, ResumeQuizAttemptData, ResumeQuizAttemptResponse } from '@edtech-ui/core/models/quiz-attempt/get-quiz-resume';
import { QuizStatus } from '@edtech-ui/constants/quiz-status.enum';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { QuestionStateService } from '@edtech-ui/core/services/question-state/question-state.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MathLatexComponent } from '@edtech-ui/shared/latex/math-latex/math-latex.component';
import { QuestionBlocksComponent } from '@edtech-ui/shared/question-blocks/question-blocks.component';

@Component({
  selector: 'app-attempt-quiz',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatProgressSpinnerModule, MatTooltipModule, MathLatexComponent, QuestionBlocksComponent],
  templateUrl: './attempt-quiz.component.html',
  styleUrls: ['./attempt-quiz.component.scss']
})
export class AttemptQuizComponent implements OnInit, OnDestroy {
  currentQuestion = 0;
  quizId: string | null = null;
  attemptId: string | null = null;
  attemptedQuestions: number = 0;
  skippedQuestions: number = 0;
  quizData: GetQuizByIdResponse['Data'] | null = null;
  classroomId: string | null = null;
  totalTime: number = 0;
  remainingTime: number = 0;
  timerSubscription: Subscription | null = null;
  formattedTime: string = '';
  answers: (string | null)[] = [];
  questions: QuizQuestionWithAnswer[] = [];

  quizForm!: FormGroup;
  resumeData!: ResumeQuizAttemptData;
  autoSaveSub: Subscription | null =null;
	quizSubmitted = false; 
	quizStatus: QuizStatus = QuizStatus.ALL;
	quizStatusEnum = QuizStatus;
	isLoading = false;
	isQuizLoading = false;
	subscription =new Subscription();
	classId: string | null = null;
    courseId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private quizService: QuizManagementService,
    private fb: FormBuilder,
    private toastr: ToastrService,
	private _questionStateService:QuestionStateService,
  ) { }

  ngOnInit() {
	  this.quizForm = this.fb.group({ questions: this.fb.array([]) }); 
    this.quizId = this.route.snapshot.paramMap.get('quizId');
    this.attemptId = this.route.snapshot.queryParamMap.get('attemptId');
	 this.classId = this.route.parent?.snapshot.paramMap.get('classId') ?? null;
    this.courseId = this.route.parent?.snapshot.paramMap.get('courseId') ?? null;

	const isResume = history.state?.isResume === true;

		if (this.attemptId && isResume) {
			this.quizStatus = QuizStatus.IN_PROGRESS;
			this.getQuizResumeAttempt(this.attemptId);
		} else {
			this.fetchQuizDetails();
  }
  this.autoSaveSub = interval(5000).subscribe(() => { this.patchSaveQuizProgress() });
}

  fetchQuizDetails(): void {
	this.isQuizLoading = true ;
    if (this.quizId) {
		this.subscription.add(
      this.quizService.getQuizById(this.quizId).pipe(
		finalize(()=>{
			this.isQuizLoading = false;
		})
	  ).subscribe({
        next: (response) => {
          if (response.Success && response.Data) {
            this.quizData = response.Data;
            this.questions = response.Data.questions || [];
            this.questions.forEach((q) => {
              q.is_skipped = false;
              q.topic = q.topic || 'General';
            });
            const parentId = this.route.parent?.snapshot.paramMap.get('id');
            this.classroomId = parentId !== undefined ? parentId : (this.quizData ? this.quizData.classroom_id?.toString() : null);
            this.quizForm = this.fb.group({
              questions: this.fb.array(
                this.questions.map((question) =>
                  this.fb.group({
                    answer: [null],
                    question_id: [question.question_id]
                  })
                )
              )
            });
			
			  this.questions.forEach((q, index) => {
				  const savedAnswer = this._questionStateService.getAnswer(q.question_id);
				  const questionForm = (this.quizForm.get('questions') as FormArray).at(index) as FormGroup;

				  if (savedAnswer) {
					  q.user_answer = savedAnswer;
					  questionForm.get('answer')?.setValue(savedAnswer);
				  } else if (q.user_answer) {
					  questionForm.get('answer')?.setValue(q.user_answer);
				  }
            });

            this.answers = new Array(this.questions.length).fill(null);
			this.totalTime = response.Data.time_limit ?? 0;
            this.remainingTime = this.totalTime;
            this.updateFormattedTime(); 
             this.startTimer();
	this.attemptId = this.quizService.quizAttemptId.getValue() || localStorage.getItem('attempt_id');
          }
        },
        error: () => {
          this.toastr.error('Failed to load quiz details. Please try again.', 'Error', { timeOut: 3000 });
        }
      }));
    }
  }

  getQuestionForm(index: number): FormGroup {
    return (this.quizForm.get('questions') as FormArray).at(index) as FormGroup;
  }

  startTimer() {
      if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }
    this.updateFormattedTime();
    this.timerSubscription = interval(1000).subscribe(() => {
      if (this.remainingTime > 0) {
        this.remainingTime--;
        this.updateFormattedTime();
      } else {
        this.timerSubscription?.unsubscribe();
        this.handleTimeUp();
      }
    });
  }

  updateFormattedTime() {
    const hours = Math.floor(this.remainingTime / 3600);
    const minutes = Math.floor((this.remainingTime % 3600) / 60);
    const seconds = this.remainingTime % 60;
    this.formattedTime = `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  pad(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  nextQuestion() {
    if (this.currentQuestion < this.questions.length - 1) {
			this.currentQuestion++;
			this._questionStateService.setLastVisitedIndex(this.currentQuestion);
		}
	}

	prevQuestion() {
		if (this.currentQuestion > 0) {
			this.currentQuestion--;
			this._questionStateService.setLastVisitedIndex(this.currentQuestion);
		}
	}

	get progressPercentage() {
		if (!this.questions.length) return 0;
		let lastVisitedIndex: number;
		if (this.quizStatus === QuizStatus.IN_PROGRESS && this._questionStateService.getLastVisitedIndex() !== null) {
			lastVisitedIndex = this._questionStateService.getLastVisitedIndex()!;
		} else {
			lastVisitedIndex = this.currentQuestion;
		}
		return ((lastVisitedIndex + 1) / this.questions.length) * 100;
  }

submitQuiz() {
  if (!this.quizForm || !this.attemptId) {
    this.toastr.warning('Quiz is not ready yet!');
    return;
  }

  if ((!this.quizData || !this.quizData.quiz_id) && !this.resumeData) {
    this.toastr.warning('Quiz data not loaded yet. Please wait.');
    return;
    }

    const answers = this.quizForm.value.questions.map((q: any) => q.answer);
    const payload = {
      attempt_id: this.attemptId,
   quiz_question_id: this.quizData?.quiz_id || this.resumeData?.quiz_id,
      quiz_conversation: this.questions.map((q, index) => ({
      questions: { ...q },
        user_answer: answers[index] ?? '',
        is_skipped: !answers[index]
      }))
    };

    const dialogRef = this.dialog.open(SubmitConfirmDialogComponent, {
      width: '400px',
      panelClass: 'custom-dialog-container',
      disableClose: true,
    data: { remainingTime: this.remainingTime }
        });

  this.quizSubmitted = true;
  this.stopTimer();

	dialogRef.afterClosed().subscribe((result) => {
		if (!result) {
			this.quizSubmitted = false;
			this.startTimer();
			return;
		}

		if (result.action === 'goBack') {
			this.quizSubmitted = false;
			this.startTimer();
			return;
		}

		if (result.action === 'submit') {
			this.isLoading = true;

			this.stopAutoSave();
            this.subscription.add(
			this.quizService.postAIQuestionsEvaluator(payload).pipe(
				timeout(60000),
				retry(2),
				catchError((err) => {
					this.isLoading = false;
			const errorMessage = err.name === 'TimeoutError'
				? 'Server took too long to respond. Please try again later.'
				: 'An error occurred while submitting the quiz. Please try again.';
			this.toastr.error(errorMessage, 'Error', { timeOut: 5000 });
			this.quizSubmitted = false;
			this.startTimer();
            this.autoSaveSub = interval(5000).subscribe(() => this.patchSaveQuizProgress());
			return throwError(() => err);
		})
	  ).subscribe({
		  next: (response) => {
			  this.isLoading = false;
			  if (response.Success) {
				  const elapsedTime = this.totalTime - this.remainingTime;
				  const finishedTime = this.formatTime(elapsedTime);
				  this.openResultDialog(finishedTime, response);
				  localStorage.removeItem('attempt_id');
			  } else {
				  this.toastr.error(response.Message || 'Failed to submit quiz', 'Error');
				  this.quizSubmitted = false;
				  this.startTimer();
				  this.autoSaveSub = interval(5000).subscribe(() => this.patchSaveQuizProgress());
			  }
		  },
		  error: () => {
			  this.isLoading = false;
			  this.toastr.error('An unexpected error occurred.', 'Error');
			  this.quizSubmitted = false;
			  this.startTimer();
			  this.autoSaveSub = interval(5000).subscribe(() => this.patchSaveQuizProgress());
		  }
	  }));
		}
	});
}


  private handleTimeUp(): void {
	  if (this.quizSubmitted) return;
	  this.quizSubmitted = true;
	    this.stopAutoSave();
    const dialogRef = this.dialog.open(TimeUpDialogComponent, {
      width: '400px',
      panelClass: 'custom-dialog-container',
      disableClose: true,
      data: { result: null, submitting: true, classroomId: this.classroomId }
    });

    const answers = this.quizForm.value.questions.map((q: any) => q.answer);

    const payload = {
      attempt_id: this.attemptId,
      quiz_question_id: this.quizData?.quiz_id || this.resumeData?.quiz_id,
      quiz_conversation: this.questions.map((q, index) => ({
        questions: {
          question_id: q.question_id,
          question_type: q.question_type || 'MCQ',
          difficulty_level: q.difficulty_level || 'Easy',
          question_text: q.question_text || '',
          options: q.question_type === 'TRUE_FALSE' ? [
            { option_id: 'true', text: 'True', order: 1 },
            { option_id: 'false', text: 'False', order: 2 }
          ] : q.question_type === 'OPEN_ENDED' ? [] : q.options || [],
          correct_answer: q.correct_answer || '',
          marks: q.marks || 1,
          order: q.order || index + 1,
          is_ai: q.is_ai ?? true,
          topic: q.topic || 'General'
        },
        user_answer: answers[index] ?? '',
        is_skipped: !answers[index]
      }))
    };

    this.quizService.postAIQuestionsEvaluator(payload).pipe(
      timeout(60000),
      retry(2),
		catchError((err) => {
			this.toastr.error('An error occurred while submitting the quiz. Please try again.', 'Error', { timeOut: 5000 });
			dialogRef.componentInstance.data = { result: null, error: true, submitting: false, classroomId: this.classroomId };
			return throwError(() => err);
		})
	).subscribe({
		next: (response) => {
			if (response.Success && response.Data) {
				dialogRef.componentInstance.data = { result: response.Data, submitting: false, classroomId: this.classroomId };
			} else if (!response.Success) {
			dialogRef.componentInstance.data = { result: null, error: true, submitting: false, classroomId: this.classroomId };
				timer(5000).subscribe(() => {
				dialogRef.close();
				this.router.navigate(['/student-dashboard','course-detail-page', this.classId, this.courseId, 'detailed-solutions', this.attemptId]);
				});
			} else {
				dialogRef.close();
				this.toastr.error('Failed to submit quiz automatically: ' + response.Message, 'Error');
				dialogRef.componentInstance.data = { result: null, error: true, submitting: false, classroomId: this.classroomId };
			}

			dialogRef.afterClosed().subscribe((result) => {
				if (result && result.action === 'viewResult') {
					this.openResultDialog(this.formatTime(this.totalTime), { Data: result.result || null });
				}
			});
		}

	});
	}

  formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(secs)}`;
  }

  openResultDialog(finishedTime: string, apiResponse: any) {
    this.dialog.open(QuizResultDialogComponent, {
	    disableClose: true,
      width: 'auto',
      panelClass: 'quiz-result-dialog-container',
      data: {
        finishedTime,
        attemptId: this.attemptId,
        result: apiResponse.Data,
		classId:this.classId,
		courseId:this.courseId
      }
    });
  }

	getQuizResumeAttempt(attemptedId: string) {
		this.isQuizLoading = true;
		this.subscription.add(
		this.quizService.getQuizResumeAttempt(attemptedId).pipe(
			 delay(500),
			finalize(()=>{ this.isQuizLoading = false })
		).subscribe({
			next: (response: ResumeQuizAttemptResponse) => {
				if (response?.Success && response.Data) {
					this.resumeData = response.Data
					this.questions = this.resumeData.quiz_conversation.map((q: any) => ({
						...q.questions,
						user_answer: q.user_answer,
						is_skipped: q.is_skipped
					}))
					const questionFormArray = this.fb.array(
						this.questions.map((q) =>
							this.fb.group({
								answer: [q.user_answer ?? null],
								question_id: [q.question_id]
							})
						)
					);
					this.quizForm = this.fb.group({ questions: questionFormArray });
					this.questions.forEach((q, index) => {
						const questionForm = (this.quizForm.get('questions') as FormArray).at(index) as FormGroup;
						const savedAnswer = this._questionStateService.getAnswer(q.question_id);

						if (savedAnswer) {
							q.user_answer = savedAnswer;
							questionForm.get('answer')?.setValue(savedAnswer);
						} else if (q.user_answer) {
							questionForm.get('answer')?.setValue(q.user_answer);
						} 
					});
					const lastVisitedIndex = this._questionStateService.getLastVisitedIndex();
					this.currentQuestion = (lastVisitedIndex !== null && lastVisitedIndex < this.questions.length) ? lastVisitedIndex : 0;

					this.attemptedQuestions = this.questions.filter(q => !!q.user_answer).length;
					this.skippedQuestions = this.questions.filter(q => q.is_skipped).length;

					const endTimeStr = this.resumeData.end_time;
					const endTime = new Date(endTimeStr + 'Z').getTime();
					const now = Date.now();
					this.remainingTime = Math.max(0, Math.floor((endTime - now) / 1000));

					this.updateFormattedTime();

					if (this.remainingTime > 0) {
						this.startTimer();
					}else {
                    this.handleTimeUp();
                }
					this.quizSubmitted = false;
					if (this.autoSaveSub) {
						this.autoSaveSub.unsubscribe();
					}
					this.autoSaveSub = interval(5000).subscribe(() => this.patchSaveQuizProgress());
				}
				else {
					this.toastr.warning(response.Message);
					this.router.navigate(['/student-dashboard/detailed-solutions', this.attemptId,]);
				}
			}
		}));
	}

	patchSaveQuizProgress() {
		if (!this.attemptId || (!this.quizData && !this.resumeData)) return;

		const quizId = this.quizData?.quiz_id || this.resumeData?.quiz_id;
		if (!quizId) {
			this.toastr.warning('Quiz data not loaded yet, skipping autosave.');
			return;
		}

		const payload: SaveQuizProgressPayload = {
			attempt_id: this.attemptId,
			quiz_question_id: quizId,
			quiz_conversation: this.questions.map((q, index) => {
				const userAnswer = this.getQuestionForm(index).get('answer')?.value;

				return {
					questions: {
						question_id: q.question_id,
						question_type: q.question_type,
						difficulty_level: q.difficulty_level,
						question_text: q.question_text,
						options: q.options || [],
						correct_answer: q.correct_answer,
						marks: q.marks,
						order: q.order,
						is_ai: q.is_ai ?? false,
						topic: q.topic || 'General'
					},
					user_answer: userAnswer ?? null,
					is_skipped: !userAnswer,
					evaluation: {
						answer_type: !userAnswer ? 'skipped' : 'partial',
						correct_answer: '',
						obtained_marks: 0,
						review: '',
						feedback: '',
						user_knowledge_level: 'beginner'
					}
				};
			})
		};
		this.subscription.add(
		this.quizService.patchSaveQuizProgress(payload).pipe(
			tap(response => {
				if (response.Success) {
					if (response.Data?.remaining_time !== undefined) {
						this.remainingTime = response.Data.remaining_time;
					}
				}
			}),
			catchError(() => {
				this.toastr.error('Failed to save quiz progress.');
				return of(null);
			})
		).subscribe());
	}

	stopTimer() {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
			this.timerSubscription = null;
		}
	}

	stopAutoSave() {
		if (this.autoSaveSub) {
			this.autoSaveSub.unsubscribe();
			this.autoSaveSub = null;
		}
	}

	onOptionSelect(option: string) {
		const currentQuestionForm = this.getQuestionForm(this.currentQuestion);
		const question = this.questions[this.currentQuestion];
		currentQuestionForm.get('answer')?.setValue(option);
		this._questionStateService.setAnswer(question.question_id, option);
	}

	ngOnDestroy() {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}
		if (this.autoSaveSub) this.autoSaveSub.unsubscribe();
		this.subscription.unsubscribe();
	}
}